import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DynamicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/pages/slug/${slug}`);
        setPage(res.data);
      } catch (error) {
        console.error('Page not found:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0f4f8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003456] border-t-transparent"></div>
      </div>
    );
  }

  if (!page) {
    return null;
  }

  // Render widget
  const renderWidget = (widget) => {
    switch (widget.type) {
      case 'heading':
        const HeadingTag = widget.props.level || 'h2';
        return (
          <HeadingTag 
            key={widget.id}
            className={`text-3xl md:text-4xl font-bold text-[#0a0203] mb-6 ${widget.props.align === 'center' ? 'text-center' : ''}`} 
            style={{  }}
          >
            {widget.props.text}
          </HeadingTag>
        );
      case 'text':
        return (
          <p 
            key={widget.id}
            className={`text-gray-600 text-lg leading-relaxed mb-6 ${widget.props.align === 'center' ? 'text-center' : ''}`}
          >
            {widget.props.text}
          </p>
        );
      case 'image':
        return (
          <div key={widget.id} className="mb-8">
            <img src={widget.props.src} alt={widget.props.alt} className="rounded-2xl w-full shadow-lg" />
            {widget.props.caption && <p className="text-sm text-gray-500 text-center mt-3">{widget.props.caption}</p>}
          </div>
        );
      case 'button':
        return (
          <div key={widget.id} className="mb-6">
            <Link to={widget.props.url || '#'}>
              <Button className={widget.props.variant === 'primary' ? 'bg-[#003456] hover:bg-[#001d30] px-8 py-6' : 'bg-gray-200 text-gray-800 hover:bg-gray-300 px-8 py-6'}>
                {widget.props.text}
              </Button>
            </Link>
          </div>
        );
      case 'columns':
        return (
          <div key={widget.id} className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="p-6 bg-white rounded-2xl shadow-sm">{widget.props.left}</div>
            <div className="p-6 bg-white rounded-2xl shadow-sm">{widget.props.right}</div>
          </div>
        );
      case 'quote':
        return (
          <blockquote key={widget.id} className="border-l-4 border-[#003456] pl-6 py-4 mb-8 bg-white rounded-r-xl">
            <p className="text-2xl italic text-gray-700 mb-3">"{widget.props.text}"</p>
            <p className="text-gray-500">— {widget.props.author}</p>
          </blockquote>
        );
      case 'video':
        return (
          <div key={widget.id} className="aspect-video rounded-2xl overflow-hidden shadow-lg mb-8">
            <iframe src={widget.props.url} className="w-full h-full" title="Video" allowFullScreen />
          </div>
        );
      case 'spacer':
        return <div key={widget.id} style={{ height: widget.props.height }} />;
      case 'divider':
        return <hr key={widget.id} className="border-gray-200 my-8" />;
      case 'list':
        return (
          <ul key={widget.id} className="space-y-3 mb-8">
            {widget.props.items?.map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-lg">
                <div className="w-6 h-6 bg-[#dbeef5] rounded-full flex items-center justify-center">
                  <Check className="h-4 w-4 text-[#003456]" />
                </div>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      case 'cta':
        return (
          <div key={widget.id} className="bg-[#003456] rounded-3xl p-12 text-center text-white mb-8">
            <h3 className="text-3xl font-bold mb-3" style={{  }}>{widget.props.heading}</h3>
            <p className="mb-6 text-white/80 text-lg">{widget.props.text}</p>
            <Link to={widget.props.buttonUrl || '/contact'}>
              <Button className="bg-white text-[#003456] hover:bg-gray-100 px-8 py-6">{widget.props.buttonText}</Button>
            </Link>
          </div>
        );
      case 'financing':
        return widget.props.embedUrl ? (
          <div key={widget.id} className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-[#0a0203]" style={{  }}>{widget.props.title}</h3>
            <div className="aspect-video rounded-2xl overflow-hidden border border-gray-200">
              <iframe src={widget.props.embedUrl} className="w-full h-full" title={widget.props.title} />
            </div>
          </div>
        ) : null;
      case 'html':
        return (
          <div 
            key={widget.id}
            className="mb-8" 
            dangerouslySetInnerHTML={{ __html: widget.props.code }} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />

      <div className="pt-24 pb-16 section-padding animate-fade-in">
        <div className="max-w-4xl mx-auto">
          {/* Page Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-[#0a0203] mb-12 text-center" style={{  }}>
            {page.title}
          </h1>

          {/* Page Content */}
          <div className="space-y-2">
            {page.sections?.map(section => (
              <div key={section.id}>
                {section.widgets?.map(widget => renderWidget(widget))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0a0203] text-white py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <LogoSmall />
            <span className="text-xl font-bold" style={{  }}>HomeLyph</span>
          </div>
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} HomeLyph. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DynamicPage;
